import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {
  StyledSearch
} from './StyledSearch';
import IconWrapper from '../IconWrapper';
import {useDebounce} from '../../hooks';
import {SearchIcon} from '../../icons';

const Search = ({
  defaultValue,
  delay = 1000,
  minSearchSymbolsLength = 1,
  onChange,
  onSearch,
  value,
  ...rest
}) => {
  const initialValue = defaultValue ?? value ?? '';
  const [searchedValue, setSearchedValue] = useState(initialValue);

  /**
   * The term the consumer already knows about, so a search is issued when the term CHANGES rather
   * than after some number of debounce cycles have gone by.
   *
   * ⚠ H1MVP-4893 — THE OLD GUARD COUNTED CYCLES AND ATE THE FIRST ONE. It was
   * `if (counterChange > 0) onSearch(…)` / `if (counterChange === 0) setCounterChange(1)`, whose
   * intent was only that mounting must not itself be a search. But `useDebounce` clears its timer on
   * every change, so a keystroke landing inside the first second restarted it — and the one cycle
   * that then completed was spent flipping the counter instead of searching. Typing stops, no second
   * cycle ever runs, and the term sits in the box having never been searched for. These boxes live
   * in a `Dropdown` with `destroyOnHidden`, so they remount on every open: the race was not once per
   * page, it was once per open, and typing straight after clicking the magnifier is what people do.
   * Measured on Vendor Bills > New — first keystroke at 600ms: no request at all; at 1400ms:
   * `?…&search=mistral`.
   *
   * Seeded with the value we mounted holding, which is what makes mounting silent without needing a
   * counter at all.
   */
  const lastSearchedValue = useRef(initialValue);

  const emitSearch = (next) => {
    lastSearchedValue.current = next;
    onSearch?.(next);
  };

  useDebounce(
    () => {
      if (!onSearch) return;
      const next = searchedValue ?? '';
      // Already asked for. Usually the parent echoing back the search we just made.
      if (next === lastSearchedValue.current) return;
      // '' is a real search — it is how the box is cleared — so only non-empty terms have a floor.
      if (next !== '' && next.length < minSearchSymbolsLength) return;
      emitSearch(next);
    },
    delay,
    [searchedValue]
  );

  useEffect(() => {
    // A value pushed down by the parent is not something to go and search for: it is the echo of a
    // search just made, or a reset the parent has already applied itself.
    if (value === undefined) return;
    setSearchedValue(value);
    lastSearchedValue.current = value;
  }, [value]);

  const handleOnChange = (e) => {
    onChange?.(e);
    setSearchedValue(e.target.value);
  }

  const handleOnSearchIconClick = () => {
    if (onSearch) emitSearch(searchedValue ?? '');
  };

  return (
    <StyledSearch
      defaultValue={defaultValue}
      suffix={<IconWrapper size='16' onClick={handleOnSearchIconClick}><SearchIcon /></IconWrapper> }
      onChange={handleOnChange}
      value={searchedValue}
      {...rest}
    />
  );
}

Search.propTypes = {
  defaultValue: PropTypes.any,
  delay: PropTypes.number,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  minSearchSymbolsLength: PropTypes.number
}

export default Search;
